<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import Sortable, { type SortableEvent } from 'sortablejs';
import { DialogClose, DialogDescription, DialogTitle } from 'reka-ui';
import { MagnifyingGlassIcon, PencilIcon, PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { GripVerticalIcon } from '@/components/ui/icons';
import type { TableColumnStaticProps } from '@/types/tableTypes';
import {
    chipLabelsOf,
    MAX_FIELD_LABEL_LENGTH,
    MAX_SECTION_LABEL_LENGTH,
    withFieldLabel,
    withNewSection,
    withoutSection,
    withSectionLabel,
    type RecordLayoutDefinition,
} from '@/composables/recordLayout/recordLayoutDefinition';
import { SAVE_FAILED_MESSAGE, type useRecordLayout } from '@/composables/recordLayout/useRecordLayout';
import {
    CHIPS_GROUP,
    customizeGroups,
    fieldCountOf,
    filterGroups,
    moveFieldOneRow,
    moveSectionOnePlace,
    NOT_SHOWN_GROUP,
    placeField,
    placeSectionAfter,
    tickField,
    untickField,
    type CustomizeGroup,
    type CustomizeRow,
} from '@/components/record/customizeList';

/**
 * Customize a record page, in the shape of Macro's "Customize sidebar": a
 * small dialog with one line of explanation, a search box, and one list.
 *
 * The list is in groups: Title, Top line, Chips, each Details section, and
 * Not shown. Every field is a row with a drag handle, a tick box and its name.
 * An empty Title group shows one muted line instead of rows, because the
 * heading then falls back to the display name.
 * A ticked row shows on the page. Unticking moves it to Not shown, and ticking
 * a row there adds it to the end of Details. Dragging a row into another group
 * moves the field there. The arrow keys on a handle do the same, one row at a
 * time, and Space on a tick box ticks it.
 *
 * Changes apply as they are made. "Just me" saves each one for you.
 * "Everyone", which only developers see, edits a draft of everyone's default
 * that the page previews until "Save as default".
 *
 * The list's rules live in customizeList.ts. This file draws them and wires up
 * dragging. Spec: docs/specs/record-header-layout.md.
 */
const props = defineProps<{
    open: boolean;
    entityId: string;
    /** The record noun as it reads mid-sentence, for example "player" or "DDEX track". */
    noun: string;
    columns: TableColumnStaticProps[];
    layoutState: ReturnType<typeof useRecordLayout>;
    isDeveloper: boolean;
}>();

const emit = defineEmits<{
    'update:open': [open: boolean];
    /** The dialog has closed and wants focus put back on whatever opened it. */
    closed: [];
}>();

const state = props.layoutState;
const mode = computed<'mine' | 'default'>(() => (state.isEditingDefault.value ? 'default' : 'mine'));
const layout = computed(() => state.shown.value);

function commit(next: RecordLayoutDefinition) {
    if (mode.value === 'default') {
        state.updateDefaultDraft(next);
    } else {
        void state.saveMine(next);
    }
}

function chooseMode(next: 'mine' | 'default') {
    if (next === mode.value) return;
    if (next === 'default') {
        state.startEditingDefault();
    } else {
        state.cancelDefault();
    }
}

const query = ref('');
const isSearching = computed(() => query.value.trim() !== '');
const allGroups = computed(() => customizeGroups(props.entityId, layout.value, props.columns));
const visibleGroups = computed(() => filterGroups(allGroups.value, query.value));
const fieldCount = computed(() => fieldCountOf(allGroups.value));

watch(
    () => props.open,
    (isOpen) => {
        if (!isOpen) query.value = '';
    },
);

/**
 * The list, flattened into the order it is drawn: each group's header, then
 * its rows. The Details options sit above the first section, and "+ New
 * section" after the last. Dragging works on this flat order, so a row can be
 * dropped into any group, including an empty one, right under its header.
 */
type ListItem =
    | { kind: 'group-header'; key: string; group: CustomizeGroup }
    | { kind: 'section-header'; key: string; group: CustomizeGroup }
    | { kind: 'row'; key: string; row: CustomizeRow; group: CustomizeGroup }
    | { kind: 'title-fallback'; key: string }
    | { kind: 'details-options'; key: string }
    | { kind: 'new-section'; key: string };

const listItems = computed<ListItem[]>(() => {
    const items: ListItem[] = [];
    const groups = visibleGroups.value;
    const lastSectionIndex = groups.map((group) => group.kind).lastIndexOf('section');
    let detailsOptionsShown = false;
    groups.forEach((group, index) => {
        if (group.kind === 'section') {
            if (!detailsOptionsShown && !isSearching.value) {
                items.push({ kind: 'details-options', key: 'details-options' });
                detailsOptionsShown = true;
            }
            items.push({ kind: 'section-header', key: `header:${group.id}`, group });
        } else {
            items.push({ kind: 'group-header', key: `header:${group.id}`, group });
        }
        for (const row of group.rows) {
            items.push({ kind: 'row', key: `${group.id}:${row.field}`, row, group });
        }
        if (group.kind === 'title' && group.rows.length === 0) {
            items.push({ kind: 'title-fallback', key: 'title-fallback' });
        }
        if (index === lastSectionIndex && !isSearching.value) {
            items.push({ kind: 'new-section', key: 'new-section' });
        }
    });
    return items;
});

// ---- Ticking ----

function setTicked(row: CustomizeRow, ticked: boolean) {
    if (ticked && row.groupId === NOT_SHOWN_GROUP) {
        commit(tickField(layout.value, props.columns, row.field));
    } else if (!ticked && row.groupId !== NOT_SHOWN_GROUP) {
        commit(untickField(layout.value, props.columns, row.field, row.groupId));
    }
}

// ---- Moving with the keyboard ----

const listElement = ref<HTMLElement | null>(null);

async function focusRowHandle(groupId: string, field: string) {
    await nextTick();
    listElement.value
        ?.querySelector<HTMLElement>(`[data-kind="row"][data-group-id="${groupId}"][data-field="${field}"] .customize-handle`)
        ?.focus();
}

async function focusSectionHandle(sectionKey: string) {
    await nextTick();
    listElement.value?.querySelector<HTMLElement>(`[data-testid="customize-section-${sectionKey}-handle"]`)?.focus();
}

function moveRow(row: CustomizeRow, direction: -1 | 1) {
    const moved = moveFieldOneRow(layout.value, props.columns, visibleGroups.value, row.field, row.groupId, direction);
    if (!moved) return;
    commit(moved.layout);
    void focusRowHandle(moved.groupId, row.field);
}

function moveSection(sectionKey: string, direction: -1 | 1) {
    commit(moveSectionOnePlace(layout.value, sectionKey, direction));
    void focusSectionHandle(sectionKey);
}

// ---- Dragging ----

/** The group header at or above an element in the list, or null above the first one. */
function groupHeaderAbove(element: Element | null): HTMLElement | null {
    let candidate = element;
    while (candidate) {
        if (candidate instanceof HTMLElement && (candidate.dataset.kind === 'group-header' || candidate.dataset.kind === 'section-header')) {
            return candidate;
        }
        candidate = candidate.previousElementSibling;
    }
    return null;
}

function sectionHeaderAbove(element: Element | null): HTMLElement | null {
    let candidate = element;
    while (candidate) {
        if (candidate instanceof HTMLElement && candidate.dataset.kind === 'section-header') return candidate;
        candidate = candidate.previousElementSibling;
    }
    return null;
}

/**
 * The node right after the dragged element when the drag began. Each item of
 * the list is a Vue fragment, with empty text nodes marking its ends, so this
 * is usually the fragment's end marker. Putting the element back before it
 * puts it back inside its own fragment, where Vue expects it.
 */
let nodeAfterDragged: Node | null = null;

function onDragStart(event: SortableEvent) {
    nodeAfterDragged = event.item.nextSibling;
}

/**
 * Sortable moves the dragged element itself. Vue owns these elements, so the
 * element goes back where it was, and the layout change redraws the list.
 */
function putBack(event: SortableEvent) {
    const { item, from } = event;
    if (nodeAfterDragged && nodeAfterDragged.parentNode === from) {
        from.insertBefore(item, nodeAfterDragged);
    } else if (event.oldIndex !== undefined) {
        item.remove();
        from.insertBefore(item, from.children[event.oldIndex] ?? null);
    }
    nodeAfterDragged = null;
}

function onDragEnd(event: SortableEvent) {
    if (event.oldIndex === event.newIndex) {
        nodeAfterDragged = null;
        return;
    }
    const item = event.item;
    const header = groupHeaderAbove(item.previousElementSibling);
    const droppedGroupId = header?.dataset.groupId ?? null;
    const next = item.nextElementSibling as HTMLElement | null;
    const beforeField = next?.dataset.kind === 'row' && next.dataset.groupId === droppedGroupId ? (next.dataset.field ?? null) : null;
    const previousSection = sectionHeaderAbove(item.previousElementSibling);
    putBack(event);

    if (item.dataset.kind === 'row' && droppedGroupId) {
        const moved = placeField(layout.value, props.columns, item.dataset.field ?? '', item.dataset.groupId ?? '', {
            groupId: droppedGroupId,
            beforeField,
        });
        if (moved) commit(moved);
    } else if (item.dataset.kind === 'section-header') {
        const sections = layout.value.sections;
        // Dropped under Not shown, the section goes last.
        const afterKey =
            droppedGroupId === NOT_SHOWN_GROUP ? (sections[sections.length - 1]?.key ?? null) : (previousSection?.dataset.sectionKey ?? null);
        commit(placeSectionAfter(layout.value, item.dataset.sectionKey ?? '', afterKey));
    }
}

let sortable: Sortable | null = null;

watch(listElement, (element) => {
    sortable?.destroy();
    sortable = null;
    if (!element) return;
    const prefersCoarsePointer = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
    sortable = Sortable.create(element, {
        handle: '.customize-handle',
        animation: 150,
        // A touch screen drags by its own events, which work on phones. A mouse
        // uses the browser's drag and drop, which Playwright can drive.
        forceFallback: Boolean(prefersCoarsePointer),
        fallbackTolerance: 4,
        scroll: true,
        bubbleScroll: true,
        onStart: onDragStart,
        onEnd: onDragEnd,
    });
});

onBeforeUnmount(() => {
    sortable?.destroy();
    sortable = null;
});

// ---- Renaming a field ----

const renamingRowKey = ref<string | null>(null);
const fieldDraft = ref('');
const fieldInput = ref<HTMLInputElement[] | HTMLInputElement | null>(null);

function firstInput(refValue: HTMLInputElement[] | HTMLInputElement | null): HTMLInputElement | null {
    return Array.isArray(refValue) ? (refValue[0] ?? null) : refValue;
}

async function startRenameField(row: CustomizeRow) {
    renamingRowKey.value = `${row.groupId}:${row.field}`;
    fieldDraft.value = row.label;
    await nextTick();
    firstInput(fieldInput.value)?.select();
}

function finishRenameField(row: CustomizeRow) {
    if (renamingRowKey.value !== `${row.groupId}:${row.field}`) return;
    renamingRowKey.value = null;
    commit(withFieldLabel(layout.value, row.field, fieldDraft.value, row.ownName));
}

function isRenamable(row: CustomizeRow): boolean {
    return (row.groupId === CHIPS_GROUP || row.groupId.startsWith('section:')) && !row.missing;
}

// ---- Sections ----

const renamingSection = ref<string | null>(null);
const sectionDraft = ref('');
const sectionInput = ref<HTMLInputElement[] | HTMLInputElement | null>(null);

async function startRenameSection(sectionKey: string, label: string) {
    renamingSection.value = sectionKey;
    sectionDraft.value = label;
    await nextTick();
    firstInput(sectionInput.value)?.select();
}

function finishRenameSection() {
    const key = renamingSection.value;
    if (key === null) return;
    renamingSection.value = null;
    commit(withSectionLabel(layout.value, key, sectionDraft.value));
}

function addSection() {
    const { layout: next, key } = withNewSection(layout.value);
    commit(next);
    void startRenameSection(key, 'New section');
}

function deleteSection(sectionKey: string) {
    commit(withoutSection(layout.value, sectionKey));
}

function setGroupBy(event: Event) {
    commit({ ...layout.value, group_by: (event.target as HTMLSelectElement).value === 'none' ? 'none' : 'section' });
}

function setSort(event: Event) {
    commit({ ...layout.value, sort: (event.target as HTMLSelectElement).value === 'alpha' ? 'alpha' : 'custom' });
}

function setChipLabels(next: 'show' | 'hide') {
    commit({ ...layout.value, chip_labels: next });
}

function onCloseAutoFocus(event: Event) {
    event.preventDefault();
    emit('closed');
}

/**
 * Rows sit straight on the dialog, with a hover tint, as in the Columns panel.
 * The tints are see-through foreground, so they show on the raised surface in
 * both themes.
 */
const rowClass = 'group flex h-8 items-center gap-2 rounded-lg px-1.5 text-sm transition-colors hover:bg-foreground/[0.05] focus-within:bg-foreground/[0.05]';
const groupHeaderClass = 'flex h-8 items-end justify-between gap-2 px-1.5 pb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground';
const handleClass =
    'customize-handle flex size-5 shrink-0 cursor-grab touch-none select-none items-center justify-center rounded text-muted-foreground/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const iconButtonClass =
    'flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.08] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-30';
/** Shown on hover or keyboard focus, and always on a touch screen, which has no hover. */
const revealOnHover = 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100';
const segmentClass =
    'h-6 rounded-md px-2 text-xs text-muted-foreground transition-colors hover:text-foreground aria-checked:bg-background aria-checked:font-medium aria-checked:text-foreground aria-checked:shadow-sm aria-selected:bg-background aria-selected:font-medium aria-selected:text-foreground aria-selected:shadow-sm dark:aria-checked:bg-foreground/[0.12] dark:aria-selected:bg-foreground/[0.12]';
</script>

<template>
  <Dialog :open="open" @update:open="emit('update:open', $event)">
    <DialogContent
      hide-close-button
      data-testid="customize-dialog"
      class="flex max-h-[min(760px,calc(100dvh-4rem))] flex-col gap-0 overflow-hidden p-0 sm:max-w-[440px] max-sm:top-0 max-sm:left-0 max-sm:h-dvh max-sm:max-h-none max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none"
      @close-auto-focus="onCloseAutoFocus"
    >
      <div class="flex shrink-0 items-center gap-2 px-4 pt-4">
        <DialogClose :class="iconButtonClass" aria-label="Close" data-testid="customize-close">
          <XMarkIcon class="size-4" aria-hidden="true" />
        </DialogClose>
        <DialogTitle class="text-sm font-semibold">Customize {{ noun }} page</DialogTitle>
      </div>
      <DialogDescription class="shrink-0 px-4 pt-2 text-[13px] leading-relaxed text-muted-foreground">
        Choose what shows on {{ noun }} pages and drag to reorder. With no title, the display name is the title.
      </DialogDescription>

      <div class="shrink-0 px-4 pt-3 pb-2">
        <label class="flex h-8 items-center gap-2 rounded-lg bg-foreground/[0.06] px-2.5 focus-within:ring-1 focus-within:ring-foreground/20">
          <MagnifyingGlassIcon class="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <input
            v-model="query"
            type="search"
            :placeholder="`Search ${fieldCount} fields`"
            :aria-label="`Search ${fieldCount} fields`"
            data-testid="customize-search"
            class="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
            @keydown.esc.stop.prevent="query === '' ? emit('update:open', false) : (query = '')"
          />
          <button v-if="query" type="button" class="rounded p-0.5 text-muted-foreground hover:text-foreground" aria-label="Clear search" @click="query = ''">
            <XMarkIcon class="size-4" aria-hidden="true" />
          </button>
        </label>
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto px-3 pb-3" data-testid="customize-list-scroll">
        <div ref="listElement" class="flex flex-col" data-testid="customize-list">
          <template v-for="item in listItems" :key="item.key">
            <!-- A group's header: Title, Top line, Chips or Not shown. -->
            <div
              v-if="item.kind === 'group-header'"
              data-kind="group-header"
              :data-group-id="item.group.id"
              data-testid="customize-group-header"
              :class="groupHeaderClass"
            >
              <span>{{ item.group.kind === 'not_shown' ? `Not shown · ${item.group.rows.length}` : item.group.label }}</span>
              <div
                v-if="item.group.kind === 'chips'"
                role="radiogroup"
                aria-label="Chip labels"
                class="flex items-center gap-1.5 font-normal normal-case tracking-normal"
                data-testid="customize-chip-labels"
              >
                <span>Labels</span>
                <div class="flex gap-0.5 rounded-lg bg-foreground/[0.06] p-0.5">
                  <button
                    v-for="choice in (['show', 'hide'] as const)"
                    :key="choice"
                    type="button"
                    role="radio"
                    :aria-checked="chipLabelsOf(layout) === choice"
                    :title="choice === 'show' ? 'Show each chip\'s label' : 'Hide the labels. Hovering a chip shows its label.'"
                    :data-testid="`customize-chip-labels-${choice}`"
                    :class="segmentClass"
                    @click="setChipLabels(choice)"
                  >
                    {{ choice === 'show' ? 'On' : 'Off' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- A Details section's header. Its handle reorders the sections. -->
            <div
              v-else-if="item.kind === 'section-header'"
              data-kind="section-header"
              :data-group-id="item.group.id"
              :data-section-key="item.group.sectionKey"
              :data-testid="`customize-section-${item.group.sectionKey}`"
              :class="[groupHeaderClass, 'group items-center justify-start pb-0 pt-2']"
            >
              <button
                type="button"
                :class="[handleClass, 'section-handle']"
                :aria-label="`Reorder the ${item.group.label} section. Drag, or press the up and down arrow keys.`"
                :data-testid="`customize-section-${item.group.sectionKey}-handle`"
                @keydown.up.prevent="moveSection(item.group.sectionKey!, -1)"
                @keydown.down.prevent="moveSection(item.group.sectionKey!, 1)"
              >
                <GripVerticalIcon size="sm" />
              </button>
              <input
                v-if="renamingSection === item.group.sectionKey"
                ref="sectionInput"
                v-model="sectionDraft"
                :maxlength="MAX_SECTION_LABEL_LENGTH"
                aria-label="Section name"
                :data-testid="`customize-section-${item.group.sectionKey}-name-input`"
                class="h-7 min-w-0 flex-1 rounded-md border border-border bg-background px-1.5 text-sm font-normal normal-case tracking-normal text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
                @keydown.enter.prevent="finishRenameSection"
                @keydown.escape.stop.prevent="renamingSection = null"
                @blur="finishRenameSection"
              />
              <span v-else class="min-w-0 flex-1 truncate">Details · {{ item.group.label }}</span>
              <span v-if="renamingSection !== item.group.sectionKey" class="flex shrink-0 items-center normal-case tracking-normal" :class="revealOnHover">
                <button
                  type="button"
                  :class="iconButtonClass"
                  :aria-label="`Rename ${item.group.label}`"
                  :data-testid="`customize-section-${item.group.sectionKey}-rename`"
                  @click="startRenameSection(item.group.sectionKey!, item.group.label)"
                >
                  <PencilIcon class="size-3.5" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  :class="iconButtonClass"
                  :aria-label="`Delete ${item.group.label}. Its fields move to the section beside it.`"
                  title="Delete section. Its fields move to the section beside it."
                  :data-testid="`customize-section-${item.group.sectionKey}-delete`"
                  :disabled="layout.sections.length < 2"
                  @click="deleteSection(item.group.sectionKey!)"
                >
                  <TrashIcon class="size-3.5" aria-hidden="true" />
                </button>
              </span>
            </div>

            <!-- One field. -->
            <div
              v-else-if="item.kind === 'row'"
              data-kind="row"
              :data-group-id="item.row.groupId"
              :data-field="item.row.field"
              data-testid="customize-row"
              :class="rowClass"
            >
              <button
                type="button"
                :class="handleClass"
                :aria-label="`Move ${item.row.label}. Drag, or press the up and down arrow keys.`"
                data-testid="customize-row-handle"
                @keydown.up.prevent="moveRow(item.row, -1)"
                @keydown.down.prevent="moveRow(item.row, 1)"
              >
                <GripVerticalIcon size="sm" />
              </button>
              <Checkbox
                :model-value="item.row.groupId !== NOT_SHOWN_GROUP"
                :aria-label="`Show ${item.row.label}`"
                data-testid="customize-row-tick"
                class="size-3.5 shrink-0 rounded-[4px] [&_svg]:size-3"
                @update:model-value="(value: boolean | 'indeterminate') => setTicked(item.row, value === true)"
              />
              <input
                v-if="renamingRowKey === item.key"
                ref="fieldInput"
                v-model="fieldDraft"
                :maxlength="MAX_FIELD_LABEL_LENGTH"
                :aria-label="`New name for ${item.row.ownName}`"
                :placeholder="item.row.ownName"
                data-testid="customize-row-rename-input"
                class="h-7 min-w-0 flex-1 rounded-md border border-border bg-background px-1.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                @keydown.enter.prevent="finishRenameField(item.row)"
                @keydown.escape.stop.prevent="renamingRowKey = null"
                @blur="finishRenameField(item.row)"
              />
              <span v-else-if="item.row.missing" class="min-w-0 flex-1 truncate text-muted-foreground line-through">
                {{ item.row.label }}<span class="sr-only"> (no longer available)</span>
              </span>
              <button
                v-else-if="isRenamable(item.row)"
                type="button"
                class="min-w-0 flex-1 cursor-text truncate rounded px-0.5 text-left"
                :title="item.row.renamed ? `Renamed from ${item.row.ownName}. Click to rename.` : 'Click to rename'"
                :aria-label="`Rename ${item.row.label}`"
                data-testid="customize-row-rename"
                @click="startRenameField(item.row)"
              >
                {{ item.row.label }}<span v-if="item.row.renamed" class="ml-1.5 text-xs text-muted-foreground">{{ item.row.ownName }}</span>
              </button>
              <span
                v-else
                class="min-w-0 flex-1 truncate px-0.5"
                :class="item.row.groupId === NOT_SHOWN_GROUP ? 'text-muted-foreground' : ''"
              >{{ item.row.label }}</span>
            </div>

            <!-- An empty Title group. The heading falls back to the display name, so the
                 group says so. It is a line, not a row: nothing to tick, drag or rename. -->
            <p
              v-else-if="item.kind === 'title-fallback'"
              data-kind="title-fallback"
              data-testid="customize-title-fallback"
              class="flex h-8 items-center pr-1.5 pl-[58px] text-sm text-muted-foreground"
            >Display name (used when empty)</p>

            <!-- How Details is grouped and sorted, above the first section. -->
            <div
              v-else-if="item.kind === 'details-options'"
              data-kind="details-options"
              class="flex flex-wrap items-center gap-x-3 gap-y-1 px-1.5 pt-3 text-xs text-muted-foreground"
            >
              <label class="flex items-center gap-1.5">
                Group by
                <select
                  class="h-6 rounded-md bg-foreground/[0.06] px-1 text-xs text-foreground"
                  data-testid="customize-group-by"
                  :value="layout.group_by"
                  @change="setGroupBy"
                >
                  <option value="section">Section</option>
                  <option value="none">None</option>
                </select>
              </label>
              <label class="flex items-center gap-1.5">
                Sort
                <select class="h-6 rounded-md bg-foreground/[0.06] px-1 text-xs text-foreground" data-testid="customize-sort" :value="layout.sort" @change="setSort">
                  <option value="custom">Custom order</option>
                  <option value="alpha">A to Z</option>
                </select>
              </label>
            </div>

            <button
              v-else-if="item.kind === 'new-section'"
              type="button"
              data-kind="new-section"
              data-testid="customize-add-section"
              class="flex h-8 items-center gap-2 rounded-lg px-1.5 text-sm text-muted-foreground transition-colors hover:bg-foreground/[0.05] hover:text-foreground"
              @click="addSection"
            >
              <span class="size-5 shrink-0" aria-hidden="true" />
              <PlusIcon class="size-3.5 shrink-0" aria-hidden="true" />
              New section
            </button>
          </template>
        </div>
        <p v-if="listItems.length === 0" class="px-1.5 py-2 text-sm text-muted-foreground" data-testid="customize-no-match">
          No field matches “{{ query.trim() }}”.
        </p>
      </div>

      <footer class="flex shrink-0 flex-col gap-2 border-t border-border/60 px-4 py-3 text-sm">
        <p v-if="state.message.value" class="text-xs text-destructive-foreground" role="alert" data-testid="customize-message">
          {{ state.message.value }}
          <button
            v-if="state.message.value === SAVE_FAILED_MESSAGE && mode === 'mine'"
            type="button"
            class="ml-1 underline underline-offset-2"
            @click="state.retrySave()"
          >
            Retry
          </button>
        </p>
        <p v-if="mode === 'default'" class="rounded-lg bg-accent/15 px-3 py-2 text-xs leading-relaxed" data-testid="customize-default-warning">
          You're editing the layout everyone starts with. People who customized keep theirs until they press Reset.
        </p>
        <div class="flex items-center justify-between gap-2">
          <div v-if="isDeveloper" role="tablist" aria-label="Whose layout" class="flex gap-0.5 rounded-lg bg-foreground/[0.06] p-0.5" data-testid="customize-mode">
            <button type="button" role="tab" :aria-selected="mode === 'mine'" data-testid="customize-mode-mine" :class="segmentClass" @click="chooseMode('mine')">
              Just me
            </button>
            <button type="button" role="tab" :aria-selected="mode === 'default'" data-testid="customize-mode-default" :class="segmentClass" @click="chooseMode('default')">
              Everyone
            </button>
          </div>
          <template v-if="mode === 'mine'">
            <button
              type="button"
              data-testid="customize-reset"
              class="rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-foreground/[0.05] hover:text-foreground"
              :class="isDeveloper ? '' : '-ml-2'"
              :title="`Saved for you, on every ${noun}. Reset brings back the default.`"
              @click="state.resetMine()"
            >
              Reset to default
            </button>
            <span v-if="!isDeveloper" class="text-xs text-muted-foreground">Saved for you, on every {{ noun }}</span>
          </template>
          <div v-else class="flex items-center gap-2">
            <button
              type="button"
              data-testid="customize-cancel-default"
              class="rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-foreground/[0.05] hover:text-foreground"
              @click="state.cancelDefault()"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="customize-save-default"
              class="rounded-full bg-accent px-3 py-1 text-xs font-semibold text-accent-foreground"
              @click="state.saveDefault()"
            >
              Save as default
            </button>
          </div>
        </div>
      </footer>
    </DialogContent>
  </Dialog>
</template>
